import { z } from "zod/v4";

import {
  QUALITY_RATING_VALUES,
  SEVERITY_RATING_VALUES,
  STATEMENT_OF_PROFICIENCY_VALUES,
} from "api/memberPortal/proficiencyFeedback/proficiencyFeedback.types";
import {
  createZodEnums,
  createZodStringOption,
} from "components/Forms/utils/validation.utils";

/** Delimiter used to join/split Option[] values for API storage */
export const OPTION_DELIMITER = " | ";

const optionArray = z.array(createZodStringOption());

// ---------------------------------------------------------------------------
// Nested discriminated union: validationTeam
// ---------------------------------------------------------------------------

const ValidationTeamHadTwoWeeksSchema = z.object({
  validationTeamTwoWeeks: z.literal(true),
  validationTeamDays: z.string().nullable().optional(),
});

const ValidationTeamDidNotHaveTwoWeeksSchema = z.object({
  validationTeamTwoWeeks: z.literal(false),
  validationTeamDays: z.string().regex(/^\d+$/, "Please enter a whole number"),
});

const ValidationTeamSchema = z.discriminatedUnion(
  "validationTeamTwoWeeks",
  [ValidationTeamHadTwoWeeksSchema, ValidationTeamDidNotHaveTwoWeeksSchema],
  {
    error:
      "Please indicate if the validation team had 2 weeks to review the package",
  },
);

const ValidationTeamDraftSchema = z.object({
  validationTeamTwoWeeks: z.boolean().optional(),
  validationTeamDays: z.string().nullable().optional(),
});

// ---------------------------------------------------------------------------
// Nested discriminated union: missTdsGroup
// ---------------------------------------------------------------------------

const MissTdsYesSchema = z.object({
  missTds: z.literal(true),
  missTdsEcrs: optionArray.min(1, "Please select at least one ECR"),
});

const MissTdsNoSchema = z.object({
  missTds: z.literal(false),
  missTdsEcrs: optionArray,
});

const MissTdsSchema = z.discriminatedUnion(
  "missTds",
  [MissTdsYesSchema, MissTdsNoSchema],
  { error: "Please indicate if the VID missed any TDs" },
);

const MissTdsDraftSchema = z.object({
  missTds: z.boolean().optional(),
  missTdsEcrs: optionArray.optional(),
});

// ---------------------------------------------------------------------------
// Nested discriminated union: misinterpretPolicyGroup
// ---------------------------------------------------------------------------

const MisinterpretPolicyYesSchema = z.object({
  misinterpretPolicy: z.literal(true),
  misinterpretedPolicies: optionArray.min(
    1,
    "Please select at least one policy",
  ),
});

const MisinterpretPolicyNoSchema = z.object({
  misinterpretPolicy: z.literal(false),
  misinterpretedPolicies: optionArray,
});

const MisinterpretPolicySchema = z.discriminatedUnion(
  "misinterpretPolicy",
  [MisinterpretPolicyYesSchema, MisinterpretPolicyNoSchema],
  { error: "Please indicate if the VID misinterpreted any policies" },
);

const MisinterpretPolicyDraftSchema = z.object({
  misinterpretPolicy: z.boolean().optional(),
  misinterpretedPolicies: optionArray.optional(),
});

// ---------------------------------------------------------------------------
// Nested discriminated union: performTestsGroup
// ---------------------------------------------------------------------------

const PerformTestsYesSchema = z.object({
  performTests: z.literal(true),
  performTestsEcrs: optionArray.min(1, "Please select at least one ECR"),
});

const PerformTestsNoSchema = z.object({
  performTests: z.literal(false),
  performTestsEcrs: optionArray,
});

const PerformTestsSchema = z.discriminatedUnion(
  "performTests",
  [PerformTestsYesSchema, PerformTestsNoSchema],
  { error: "Please indicate if the VID failed to perform tests" },
);

const PerformTestsDraftSchema = z.object({
  performTests: z.boolean().optional(),
  performTestsEcrs: optionArray.optional(),
});

// ---------------------------------------------------------------------------
// Shared field definitions (all optional — each schema overrides as needed)
// ---------------------------------------------------------------------------

const optionalFormFields = {
  statementOfProficiency: createZodEnums({
    valueObject: STATEMENT_OF_PROFICIENCY_VALUES,
    allowEmptyString: true,
  })
    .nullable()
    .optional(),
  requestedNvlapFollowUp: z.boolean().optional(),
  nvlapFollowUp: z.boolean().optional(),
  potentialNonConformities: z.string().optional(),
  validationTeam: ValidationTeamDraftSchema,
  missTdsGroup: MissTdsDraftSchema,
  misinterpretPolicyGroup: MisinterpretPolicyDraftSchema,
  performTestsGroup: PerformTestsDraftSchema,
  quality: createZodEnums({ valueObject: QUALITY_RATING_VALUES })
    .nullable()
    .optional(),
  discrepancies: createZodEnums({ valueObject: SEVERITY_RATING_VALUES })
    .nullable()
    .optional(),
  evidence: createZodEnums({ valueObject: SEVERITY_RATING_VALUES })
    .nullable()
    .optional(),
  clarification: createZodEnums({ valueObject: SEVERITY_RATING_VALUES })
    .nullable()
    .optional(),
  missingItems: createZodEnums({ valueObject: SEVERITY_RATING_VALUES })
    .nullable()
    .optional(),
  comments: z.string().optional(),
};

// ---------------------------------------------------------------------------
// Final submission — all fields required
// ---------------------------------------------------------------------------

export const FinalProficiencyFeedbackFormSchema = z.object({
  formState: z.literal("finalize"),
  ...optionalFormFields,
  // Override required fields for final submission
  statementOfProficiency: createZodEnums({
    valueObject: STATEMENT_OF_PROFICIENCY_VALUES,
  }),
  requestedNvlapFollowUp: z.boolean(),
  nvlapFollowUp: z.boolean(),
  potentialNonConformities: z
    .string()
    .min(1, "Please indicate any potential non-conformities"),
  validationTeam: ValidationTeamSchema,
  missTdsGroup: MissTdsSchema,
  misinterpretPolicyGroup: MisinterpretPolicySchema,
  performTestsGroup: PerformTestsSchema,
  quality: createZodEnums({
    valueObject: QUALITY_RATING_VALUES,
    customErrorText: "Please rate the quality of package",
  }),
  discrepancies: createZodEnums({
    valueObject: SEVERITY_RATING_VALUES,
    customErrorText: "Please rate the number of discrepancies",
  }),
  evidence: createZodEnums({
    valueObject: SEVERITY_RATING_VALUES,
    customErrorText: "Please rate the incomplete evidence and test performance",
  }),
  clarification: createZodEnums({
    valueObject: SEVERITY_RATING_VALUES,
    customErrorText: "Please rate the clarification required",
  }),
  missingItems: createZodEnums({
    valueObject: SEVERITY_RATING_VALUES,
    customErrorText: "Please rate the missing documentation items",
  }),
  comments: z.string(),
});

// ---------------------------------------------------------------------------
// Draft — all fields optional, no refinements
// ---------------------------------------------------------------------------

export const DraftProficiencyFeedbackFormSchema = z.object({
  formState: z.literal("draft"),
  ...optionalFormFields,
});

// ---------------------------------------------------------------------------
// Withdrawn — missTds and misinterpretPolicy required
// ---------------------------------------------------------------------------

export const WithdrawnProficiencyFeedbackSubmitFormSchema = z.object({
  formState: z.literal("withdrawn"),
  ...optionalFormFields,
  // Override required fields for withdrawn submission
  validationTeam: ValidationTeamDraftSchema,
  missTdsGroup: MissTdsSchema,
  misinterpretPolicyGroup: MisinterpretPolicySchema,
  performTestsGroup: PerformTestsDraftSchema,
});

// ---------------------------------------------------------------------------
// Discriminated union + exports
// ---------------------------------------------------------------------------

export const ProficiencyFeedbackFormSchema = z.discriminatedUnion("formState", [
  FinalProficiencyFeedbackFormSchema,
  DraftProficiencyFeedbackFormSchema,
  WithdrawnProficiencyFeedbackSubmitFormSchema,
]);

export type ProficiencyFeedbackFormValues = z.infer<
  typeof ProficiencyFeedbackFormSchema
>;

export type ProficiencyFeedbackFormField =
  | keyof z.infer<typeof FinalProficiencyFeedbackFormSchema>
  | "validationTeam.validationTeamTwoWeeks"
  | "validationTeam.validationTeamDays"
  | "missTdsGroup.missTds"
  | "missTdsGroup.missTdsEcrs"
  | "misinterpretPolicyGroup.misinterpretPolicy"
  | "misinterpretPolicyGroup.misinterpretedPolicies"
  | "performTestsGroup.performTests"
  | "performTestsGroup.performTestsEcrs";

const optionsToString = (options: { label: string; value: string }[]) =>
  options.map((o) => o.value).join(OPTION_DELIMITER);

export const toFeedbackPayload = (values: ProficiencyFeedbackFormValues) => ({
  ...values,
  // Flatten nested groups for the API
  validationTeamTwoWeeks: values.validationTeam?.validationTeamTwoWeeks,
  validationTeamDays:
    values.validationTeam?.validationTeamDays &&
    values.validationTeam.validationTeamDays.length > 0
      ? parseInt(values.validationTeam.validationTeamDays)
      : null,
  missTds: values.missTdsGroup?.missTds,
  missTdsEcrs: optionsToString(values.missTdsGroup?.missTdsEcrs ?? []),
  misinterpretPolicy: values.misinterpretPolicyGroup?.misinterpretPolicy,
  misinterpretedPolicies: optionsToString(
    values.misinterpretPolicyGroup?.misinterpretedPolicies ?? [],
  ),
  performTests: values.performTestsGroup?.performTests,
  performTestsEcrs: optionsToString(
    values.performTestsGroup?.performTestsEcrs ?? [],
  ),
});
